import * as React from "react";

import { Checkbox, Divider, Dropdown, Grid, Message } from "semantic-ui-react";

import { CheckboxProps } from "semantic-ui-react/dist/commonjs/modules/Checkbox/Checkbox";

import { CustomQuestionConfig, QuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import {
    MasterQuestionConfig
} from "@teamprevent/common/questionnaires/questionnaireConfig/models";

import { MandatoryDobOrEmployeeIdEnum, mandatoryDobOrEmployeeIdOptions } from "../model";

interface AdditionalCustomQuestionEditProps {
    onChanged: (questionConfig: QuestionConfig) => void;
    questions: QuestionConfig[];
    masterQuestion: MasterQuestionConfig[];
}

interface CustomAdditionalQuestion {
    id: string;
    defaultMandatoryValue: boolean;
}

type CustomQuestionKeys =
    "Employee" |
    "Dob" |
    "Postcode" |
    "OrgLevel2" |
    "OrgLevel3" |
    "PurchaseOrderNumber";

type CustomAdditionalQuestions = {
    [key in CustomQuestionKeys]: CustomAdditionalQuestion;
};

const customQuestions: CustomAdditionalQuestions = {
    Employee: {
        id: "EmployeeId",
        defaultMandatoryValue: true,
    },
    Dob: {
        id: "ReferredDob",
        defaultMandatoryValue: true,
    },
    Postcode: {
        id: "Postcode",
        defaultMandatoryValue: true,
    },
    OrgLevel2: {
        id: "OrgLevel2Name",
        defaultMandatoryValue: false,
    },
    OrgLevel3: {
        id: "OrgLevel3Name",
        defaultMandatoryValue: false,
    },
    PurchaseOrderNumber: {
        id: "PurchaseOrderNumber",
        defaultMandatoryValue: false,
    }
};

export const AdditionalCustomQuestionEdit: React.FC<AdditionalCustomQuestionEditProps> = (props: AdditionalCustomQuestionEditProps) =>
{
    const getMasterQuestion = React.useCallback((id: string): MasterQuestionConfig => {
        const masterQuestion = props.masterQuestion.find(q => q.id === id);

        if (!masterQuestion) {
            throw new Error("Could not find master question");
        }

        return masterQuestion;
    }, [props.masterQuestion]);

    const onIsMandatoryChanged = React.useCallback((checkboxProps: CheckboxProps, questionId: string) => {
        const question = props.questions.find(q => q.id === questionId);
        const masterQuestion = getMasterQuestion(questionId);
        let disabled = masterQuestion.disabled;
        let questionText = masterQuestion.questionText;

        if (question) {
            const questionConfig = (question as CustomQuestionConfig);

            if (questionConfig.questionText !== null) {
                questionText = questionConfig.questionText;
            }
            if (questionConfig.disabled !== null) {
                disabled =  questionConfig.disabled;
            }
        }
        props.onChanged({
            id: masterQuestion.id,
            isMandatory: checkboxProps.checked,
            questionText,
            disabled
        });
    }, [getMasterQuestion, props]);

    const setDefaultValue = React.useCallback((question: CustomAdditionalQuestion) => {
        onIsMandatoryChanged({ checked: question.defaultMandatoryValue }, question.id);
    }, [onIsMandatoryChanged]);

    const questionIsMandatory = React.useCallback((question: CustomAdditionalQuestion): boolean => {
        const questionConfig = props.questions.find(q => q.id === question.id);

        if (!questionConfig) {
            setDefaultValue(question);
            return true;
        }

        const customConfig = (questionConfig as CustomQuestionConfig);

        if (customConfig.isMandatory !== null && customConfig.isMandatory !== undefined) {
            return customConfig.isMandatory;
        }

        setDefaultValue(question);
        return true;
    }, [props.questions, setDefaultValue]);

    const getDropDownValue = React.useCallback((): MandatoryDobOrEmployeeIdEnum => {

        const employeeIdMandatory = questionIsMandatory(customQuestions.Employee);
        const dobIdMandatory = questionIsMandatory(customQuestions.Dob);

        if (employeeIdMandatory && dobIdMandatory) {
            return MandatoryDobOrEmployeeIdEnum.BothRequired;
        }

        if (employeeIdMandatory) {
            return MandatoryDobOrEmployeeIdEnum.EmployeeUniqueIdRequired;
        }

        if (dobIdMandatory) {
            return MandatoryDobOrEmployeeIdEnum.DobRequired;
        }

        return MandatoryDobOrEmployeeIdEnum.EmployeeUniqueIdRequired;
    }, [questionIsMandatory]);

    const onMandatoryDropDownChange = React.useCallback((event: React.SyntheticEvent<HTMLElement>, { value }: any) => {

        switch (value) {
            case MandatoryDobOrEmployeeIdEnum.BothRequired:
                onIsMandatoryChanged({ checked: true }, customQuestions.Employee.id);
                onIsMandatoryChanged({ checked: true }, customQuestions.Dob.id);
                break;
            case MandatoryDobOrEmployeeIdEnum.DobRequired:
                onIsMandatoryChanged({ checked: false }, customQuestions.Employee.id);
                onIsMandatoryChanged({ checked: true }, customQuestions.Dob.id);
                break;
            case MandatoryDobOrEmployeeIdEnum.EmployeeUniqueIdRequired:
                onIsMandatoryChanged({ checked: true }, customQuestions.Employee.id);
                onIsMandatoryChanged({ checked: false }, customQuestions.Dob.id);
                break;
            default:
                throw new Error("Not handled option");
        }
    }, [onIsMandatoryChanged]);

    const onPostcodeChange: (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void = React.useCallback(
        (e, v) => onIsMandatoryChanged(v, customQuestions.Postcode.id),
        [onIsMandatoryChanged]);
    const onOrgLevel2Change: (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void = React.useCallback(
        (e, v) => onIsMandatoryChanged(v, customQuestions.OrgLevel2.id),
        [onIsMandatoryChanged]);
    const onOrgLevel3Change: (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void = React.useCallback(
        (e, v) => onIsMandatoryChanged(v, customQuestions.OrgLevel3.id),
        [onIsMandatoryChanged]);
    const onPurchaseOrderNumberChange: (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void = React.useCallback(
        (e, v) => onIsMandatoryChanged(v, customQuestions.PurchaseOrderNumber.id),
        [onIsMandatoryChanged]);

    return (
        <Grid container>
            <Message>
                <Message.Header>Mandatory fields</Message.Header>
                <p>
                   Both Employee Unique ID and Date of Birth can not be optional
                </p>
            </Message>
            <Grid.Row columns={2}>
                <Grid.Column>
                    <Dropdown
                        value={getDropDownValue()}
                        label="DOB & Employee Unique ID - Mandatory"
                        onChange={onMandatoryDropDownChange}
                        options={mandatoryDobOrEmployeeIdOptions}
                        selection
                        labeled
                    />
                </Grid.Column>
                <Grid.Column>
                    <Checkbox
                        checked={questionIsMandatory(customQuestions.Employee)}
                        label="Employee Unique ID"
                        readOnly={true}
                    />
                    <br />
                    <Checkbox
                        checked={questionIsMandatory(customQuestions.Dob)}
                        label="DOB"
                        readOnly={true}
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    <div className="ui form">
                        <div className="field">
                            <Checkbox
                                checked={questionIsMandatory(customQuestions.Postcode)}
                                label="Post code"
                                onChange={onPostcodeChange}
                            />
                        </div>
                        <div className="field">
                            <Checkbox
                                checked={questionIsMandatory(customQuestions.OrgLevel2)}
                                label="Org Level 2"
                                onChange={onOrgLevel2Change}
                            />
                        </div>
                        <div className="field">
                            <Checkbox
                                checked={questionIsMandatory(customQuestions.OrgLevel3)}
                                label="Org Level 3"
                                onChange={onOrgLevel3Change}
                            />
                        </div>
                        <div className="field">
                            <Checkbox
                                checked={questionIsMandatory(customQuestions.PurchaseOrderNumber)}
                                label="Purchase Order Number"
                                onChange={onPurchaseOrderNumberChange}
                            />
                        </div>
                    </div>
                </Grid.Column>
            </Grid.Row>
            <Divider />
        </Grid>
    );
};
